/**
 * API Service Layer
 * Handles all communication with Laravel backend
 */

/// <reference types="vite/client" />

// Secure API configuration
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000/api';
const API_TIMEOUT = 30000; // 30 seconds

// Debug: Log API URL (remove in production)
if (import.meta.env.DEV) {
  console.log('API Base URL:', API_BASE_URL);
}

// Import types
import type { User } from '@/types/index.ts';

// Types (keep local definitions for backward compatibility)
export interface ApiResponse<T> {
  data: T;
  message?: string;
  success: boolean;
}

export interface PaginatedResponse<T> {
  data: T[];
  meta?: {
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
    from?: number;
    to?: number;
  };
  links?: {
    first?: string;
    last?: string;
    prev?: string | null;
    next?: string | null;
  };
  // Legacy support
  current_page?: number;
  last_page?: number;
  per_page?: number;
  total?: number;
}

// Secure token storage with encryption key (in production, use secure storage)
const TOKEN_KEY = 'authToken';
const USER_KEY = 'userData';
const TOKEN_EXPIRY_KEY = 'tokenExpiry';

// Get auth token from secure storage
export const getAuthToken = (): string | null => {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch (error) {
    console.error('Error reading token:', error);
    return null;
  }
};

// Store user data securely
export const storeUserData = (user: User, token: string, expiresAt?: string): void => {
  try {
    localStorage.setItem(TOKEN_KEY, token);
    localStorage.setItem(USER_KEY, JSON.stringify(user));
    if (expiresAt) {
      localStorage.setItem(TOKEN_EXPIRY_KEY, expiresAt);
    }
  } catch (error) {
    console.error('Error storing user data:', error);
  }
};

// Get token expiration time
export const getTokenExpiry = (): number | null => {
  try {
    const expiry = localStorage.getItem(TOKEN_EXPIRY_KEY);
    return expiry ? new Date(expiry).getTime() : null;
  } catch (error) {
    console.error('Error reading token expiry:', error);
    return null;
  }
};

// Check if token is expired or will expire soon
export const isTokenExpiringSoon = (bufferMinutes: number = 5): boolean => {
  const expiry = getTokenExpiry();
  if (!expiry) return false;
  const bufferMs = bufferMinutes * 60 * 1000;
  return Date.now() >= (expiry - bufferMs);
};

// Get user data
export const getUserData = (): User | null => {
  try {
    const userData = localStorage.getItem(USER_KEY);
    return userData ? JSON.parse(userData) : null;
  } catch (error) {
    console.error('Error reading user data:', error);
    return null;
  }
};

// Clear all auth data
export const clearAuthData = (): void => {
  try {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    localStorage.removeItem(TOKEN_EXPIRY_KEY);
    localStorage.removeItem('userEmail');
    localStorage.removeItem('userRole');
  } catch (error) {
    console.error('Error clearing auth data:', error);
  }
};

// Create headers with authentication
const getHeaders = (includeAuth: boolean = true): HeadersInit => {
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    'X-Requested-With': 'XMLHttpRequest',
  };

  if (includeAuth) {
    const token = getAuthToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
  }

  return headers;
};

// Request timeout handler
const fetchWithTimeout = (url: string, options: RequestInit, timeout: number): Promise<Response> => {
  return Promise.race([
    fetch(url, options).catch((error) => {
      // Re-throw with more context
      if (error.message === 'Failed to fetch' || error.name === 'TypeError') {
        const isDevelopment = import.meta.env.DEV;
        const errorMsg = isDevelopment
          ? `Unable to connect to server. Please check your internet connection and try again.`
          : `Unable to connect to server. Please try again later or contact support if the problem persists.`;
        throw new Error(errorMsg);
      }
      throw error;
    }),
    new Promise<Response>((_, reject) =>
      setTimeout(() => reject(new Error('Request timeout')), timeout)
    ),
  ]);
};

// Handle API errors
const handleResponse = async <T>(response: Response): Promise<ApiResponse<T>> => {
  // Handle non-JSON responses
  const contentType = response.headers.get('content-type');
  if (!contentType || !contentType.includes('application/json')) {
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return { data: {} as T, success: true };
  }

  const data = await response.json();

  if (!response.ok) {
    // Handle authentication errors
    if (response.status === 401) {
      clearAuthData();
      // Only redirect if not already on login page
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
      throw new Error('Invalid credentials! Try again.');
    }

    // Handle validation errors
    if (response.status === 422 && data.errors) {
      const errorMessages = Object.values(data.errors).flat().join(', ');
      throw new Error(errorMessages || 'Validation failed');
    }

    throw new Error(data.message || `An error occurred (${response.status})`);
  }

  return data;
};

// API Client with timeout and error handling
export const api = {
  // GET request
  get: async <T>(endpoint: string, includeAuth: boolean = true): Promise<ApiResponse<T>> => {
    try {
      const url = `${API_BASE_URL}${endpoint}`;
      
      if (import.meta.env.DEV) {
        console.log('🔵 API GET Request:', url);
      }
      
      const response = await fetchWithTimeout(
        url,
        {
          method: 'GET',
          headers: getHeaders(includeAuth),
          // Remove credentials for API-only auth
          // credentials: 'include',
        },
        API_TIMEOUT
      );

      return handleResponse<T>(response);
    } catch (error: any) {
      if (error.message === 'Request timeout') {
        throw new Error('Request timed out. Please try again.');
      }
      if (error.message === 'Failed to fetch' || error.name === 'TypeError') {
        const isDevelopment = import.meta.env.DEV;
        const errorMsg = isDevelopment
          ? 'Unable to connect to server. Please check your internet connection and try again.'
          : 'Unable to connect to server. Please try again later or contact support if the problem persists.';
        throw new Error(errorMsg);
      }
      throw error;
    }
  },

  // POST request
  post: async <T>(endpoint: string, data: any, includeAuth: boolean = true): Promise<ApiResponse<T>> => {
    const url = `${API_BASE_URL}${endpoint}`;
    
    // Debug logging in development
    if (import.meta.env.DEV) {
      console.log('🔵 API POST Request:', url);
      console.log('🔵 Request Data:', data);
    }
    
    try {
      const response = await fetchWithTimeout(
        url,
        {
          method: 'POST',
          headers: getHeaders(includeAuth),
          body: JSON.stringify(data),
          // Remove credentials for API-only auth (causes CORS issues)
          // credentials: 'include',
        },
        API_TIMEOUT
      );

      if (import.meta.env.DEV) {
        console.log('API Response Status:', response.status);
      }

      return handleResponse<T>(response);
    } catch (error: any) {
      // Enhanced error logging
      if (import.meta.env.DEV) {
        console.error('🔴 API POST Error:', {
          url,
          error: error.message,
          name: error.name,
          type: typeof error
        });
      }
      
      if (error.message === 'Request timeout') {
        throw new Error('Request timed out. Please try again.');
      }
      if (error.message === 'Failed to fetch' || error.name === 'TypeError' || error.message.includes('Network error')) {
        // Production-ready error message
        const isDevelopment = import.meta.env.DEV;
        const errorMsg = isDevelopment
          ? `Unable to connect to server. Please check your internet connection and try again.`
          : `Unable to connect to server. Please try again later or contact support if the problem persists.`;
        throw new Error(errorMsg);
      }
      throw error;
    }
  },

  // PUT request
  put: async <T>(endpoint: string, data: any, includeAuth: boolean = true): Promise<ApiResponse<T>> => {
    const url = `${API_BASE_URL}${endpoint}`;
    
    // Debug logging in development
    if (import.meta.env.DEV) {
      console.log('🟣 API PUT Request:', url);
      console.log('🟣 Request Data:', data);
    }
    
    try {
      const response = await fetchWithTimeout(
        url,
        {
          method: 'PUT',
          headers: getHeaders(includeAuth),
          body: JSON.stringify(data),
          // Removed credentials: 'include' for API-only auth (causes CORS issues)
        },
        API_TIMEOUT
      );

      if (import.meta.env.DEV) {
        console.log('🟢 API Response Status:', response.status);
      }

      return handleResponse<T>(response);
    } catch (error: any) {
      // Enhanced error logging
      if (import.meta.env.DEV) {
        console.error('API PUT Error:', {
          url,
          error: error.message,
          name: error.name,
          type: typeof error
        });
      }
      
      if (error.message === 'Request timeout') {
        throw new Error('Request timed out. Please try again.');
      }
      if (error.message === 'Failed to fetch' || error.name === 'TypeError' || error.message.includes('Network error')) {
        // Production-ready error message
        const isDevelopment = import.meta.env.DEV;
        const errorMsg = isDevelopment
          ? `Unable to connect to server. Please check your internet connection and try again.`
          : `Unable to connect to server. Please try again later or contact support if the problem persists.`;
        throw new Error(errorMsg);
      }
      throw error;
    }
  },

  // DELETE request
  delete: async <T>(endpoint: string, includeAuth: boolean = true): Promise<ApiResponse<T>> => {
    const url = `${API_BASE_URL}${endpoint}`;
    
    // Debug logging in development
    if (import.meta.env.DEV) {
      console.log('🔴 API DELETE Request:', url);
    }
    
    try {
      const response = await fetchWithTimeout(
        url,
        {
          method: 'DELETE',
          headers: getHeaders(includeAuth),
          // Remove credentials for API-only auth (causes CORS issues)
          // credentials: 'include',
        },
        API_TIMEOUT
      );

      if (import.meta.env.DEV) {
        console.log('API DELETE Response Status:', response.status);
      }

      return handleResponse<T>(response);
    } catch (error: any) {
      // Enhanced error logging
      if (import.meta.env.DEV) {
        console.error('API DELETE Error:', {
          url,
          error: error.message,
          name: error.name,
          type: typeof error
        });
      }
      
      if (error.message === 'Request timeout') {
        throw new Error('Request timed out. Please try again.');
      }
      if (error.message === 'Failed to fetch' || error.name === 'TypeError' || error.message.includes('Network error')) {
        const isDevelopment = import.meta.env.DEV;
        const errorMsg = isDevelopment
          ? `Unable to connect to server. Please check your internet connection and try again.`
          : `Unable to connect to server. Please try again later or contact support if the problem persists.`;
        throw new Error(errorMsg);
      }
      throw error;
    }
  },

  // PATCH request
  patch: async <T>(endpoint: string, data?: any, includeAuth: boolean = true): Promise<ApiResponse<T>> => {
    const url = `${API_BASE_URL}${endpoint}`;
    
    // Debug logging in development
    if (import.meta.env.DEV) {
      console.log('🟡 API PATCH Request:', url);
      if (data) {
        console.log('🟡 Request Data:', data);
      }
    }
    
    try {
      const response = await fetchWithTimeout(
        url,
        {
          method: 'PATCH',
          headers: getHeaders(includeAuth),
          body: data ? JSON.stringify(data) : undefined,
        },
        API_TIMEOUT
      );

      if (import.meta.env.DEV) {
        console.log('API PATCH Response Status:', response.status);
      }

      return handleResponse<T>(response);
    } catch (error: any) {
      // Enhanced error logging
      if (import.meta.env.DEV) {
        console.error('API PATCH Error:', {
          url,
          error: error.message,
          name: error.name,
          type: typeof error
        });
      }
      
      if (error.message === 'Request timeout') {
        throw new Error('Request timed out. Please try again.');
      }
      if (error.message === 'Failed to fetch' || error.name === 'TypeError' || error.message.includes('Network error')) {
        const isDevelopment = import.meta.env.DEV;
        const errorMsg = isDevelopment
          ? `Unable to connect to server. Please check your internet connection and try again.`
          : `Unable to connect to server. Please try again later or contact support if the problem persists.`;
        throw new Error(errorMsg);
      }
      throw error;
    }
  },
};

// Authentication API
export const authApi = {
  login: async (email: string, password: string) => {
    const loginData: any = { 
      email, 
      password,
      remember_me: false 
    };
    // Don't send role - backend determines admin/staff from user's role in database

    const response = await api.post<{
      token: string;
      token_type: string;
      expires_at?: string;
      user: {
        id: number;
        email: string;
        name: string;
        role: string;
        status?: string;
      };
    }>('/v1/auth/login', loginData, false);

    // Store token and user info securely
    if (response.data.token && response.data.user) {
      storeUserData(
        response.data.user as User, 
        response.data.token,
        response.data.expires_at
      );
    }

    return response.data;
  },

  register: async (name: string, email: string, password: string, password_confirmation: string) => {
    const response = await api.post<{
      token: string;
      token_type: string;
      expires_at?: string;
      user: {
        id: number;
        email: string;
        name: string;
        role: string;
      };
    }>('/v1/auth/register', {
      name,
      email,
      password,
      password_confirmation,
    }, false);

    // Store token and user info securely
    if (response.data.token && response.data.user) {
      storeUserData(
        response.data.user as User, 
        response.data.token,
        response.data.expires_at
      );
    }

    return response.data;
  },

  logout: async () => {
    try {
      await api.post('/v1/auth/logout', {});
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      clearAuthData();
    }
  },

  refresh: async () => {
    const response = await api.post<{
      token: string;
      token_type: string;
      expires_at?: string;
    }>('/v1/auth/refresh', {});
    
    // Update stored token
    const token = response.data.token;
    if (token) {
      const userData = getUserData();
      if (userData) {
        storeUserData(userData, token, response.data.expires_at);
      }
    }
    
    return response.data;
  },

  getCurrentUser: async () => {
    const response = await api.get<{
      user: {
        id: number;
        email: string;
        name: string;
        role: string;
        status?: string;
      };
    }>('/v1/auth/me');

    // Backend returns { user: {...} } structure
    const userData = (response.data as any)?.user || response.data;

    // Update stored user data (preserve existing token expiry)
    if (userData) {
      const token = getAuthToken();
      const existingExpiry = getTokenExpiry();
      if (token) {
        storeUserData(
          userData as User, 
          token,
          existingExpiry ? new Date(existingExpiry).toISOString() : undefined
        );
      }
    }

    return userData as User;
  },

  updateProfile: async (data: { name?: string; email?: string }) => {
    // Validate input on frontend
    if (data.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email)) {
      throw new Error('Please enter a valid email address');
    }
    
    if (data.name && data.name.trim().length < 2) {
      throw new Error('Name must be at least 2 characters');
    }

    const response = await api.put<{
      id: number;
      email: string;
      name: string;
      role: string;
      status?: string;
    }>('/v1/auth/profile', data);

    // Update stored user data (preserve existing token expiry)
    if (response.data) {
      const token = getAuthToken();
      const existingExpiry = getTokenExpiry();
      if (token) {
        storeUserData(
          response.data as User, 
          token,
          existingExpiry ? new Date(existingExpiry).toISOString() : undefined
        );
      }
    }

    return response.data;
  },

  updatePassword: async (current_password: string, password: string, password_confirmation: string) => {
    return api.put('/v1/auth/password', {
      current_password,
      password,
      password_confirmation,
    });
  },

  forgotPassword: async (email: string) => {
    const response = await api.post<{
      message?: string;
      reset_token?: string; // Only in development
      email?: string;
    }>('/v1/auth/forgot-password', { email }, false);
    return response.data;
  },

  resetPassword: async (email: string, token: string, password: string, password_confirmation: string) => {
    return api.post('/v1/auth/reset-password', {
      email,
      token,
      password,
      password_confirmation,
    }, false);
  },
};

// Inventory API
export const inventoryApi = {
  getItems: async (params?: {
    search?: string;
    category?: string;
    category_id?: number;
    status?: string;
    low_stock?: boolean;
    page?: number;
    per_page?: number;
  }) => {
    const queryParams = new URLSearchParams();
    if (params?.search) queryParams.append('search', params.search);
    if (params?.category) queryParams.append('category', params.category);
    if (params?.category_id) queryParams.append('category_id', params.category_id.toString());
    if (params?.status) queryParams.append('status', params.status);
    if (params?.low_stock !== undefined) queryParams.append('low_stock', params.low_stock.toString());
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.per_page) queryParams.append('per_page', params.per_page.toString());

    const queryString = queryParams.toString();
    const endpoint = `/v1/inventory${queryString ? `?${queryString}` : ''}`;

    return api.get<PaginatedResponse<any>>(endpoint);
  },

  getItem: async (id: number) => {
    return api.get<any>(`/v1/inventory/${id}`);
  },

  createItem: async (data: {
    name: string;
    description?: string;
    barcode?: string;
    sku?: string;
    category_id?: number;
    location_id?: number;
    quantity?: number;
    min_quantity?: number;
    max_quantity?: number;
    unit_price?: number;
    unit?: string;
    expiry_date?: string;
    status?: string;
  }) => {
    return api.post<any>('/v1/inventory', data);
  },

  updateItem: async (id: number, data: Partial<{
    name: string;
    sku: string;
    barcode: string;
    category: string;
    quantity: number;
    minStock: number;
    unit: string;
    location: string;
  }>) => {
    return api.put<any>(`/v1/inventory/${id}`, data);
  },

  deleteItem: async (id: number) => {
    return api.delete<any>(`/v1/inventory/${id}`);
  },

  searchByBarcode: async (barcode: string) => {
    return api.get<any>(`/v1/inventory/barcode/${barcode}`);
  },

  generateBarcode: async () => {
    return api.post<{ barcode: string }>('/v1/inventory/generate-barcode', {});
  },
};

// Categories API
export const categoryApi = {
  getCategories: async (params?: {
    search?: string;
    page?: number;
    per_page?: number;
    status?: 'active' | 'inactive';
  }) => {
    const queryParams = new URLSearchParams();
    if (params?.search) queryParams.append('search', params.search);
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.per_page) queryParams.append('per_page', params.per_page.toString());
    if (params?.status) queryParams.append('status', params.status);

    const queryString = queryParams.toString();
    const endpoint = `/v1/categories${queryString ? `?${queryString}` : ''}`;

    return api.get<PaginatedResponse<any>>(endpoint);
  },

  getCategory: async (id: number) => {
    return api.get<any>(`/v1/categories/${id}`);
  },

  createCategory: async (data: { name: string; description?: string; is_active?: boolean }) => {
    return api.post<any>('/v1/categories', data);
  },

  updateCategory: async (id: number, data: { name?: string; description?: string; is_active?: boolean }) => {
    return api.put<any>(`/v1/categories/${id}`, data);
  },

  deleteCategory: async (id: number) => {
    return api.delete<any>(`/v1/categories/${id}`);
  },

  toggleCategoryActive: async (id: number) => {
    return api.patch<any>(`/v1/categories/${id}/toggle-active`);
  },
};

// Locations API
export const locationApi = {
  getLocations: async (params?: {
    search?: string;
    page?: number;
    per_page?: number;
    type?: 'receiving' | 'issuing';
  }) => {
    const queryParams = new URLSearchParams();
    if (params?.search) queryParams.append('search', params.search);
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.per_page) queryParams.append('per_page', params.per_page.toString());
    if (params?.type) queryParams.append('type', params.type);

    const queryString = queryParams.toString();
    const endpoint = `/v1/locations${queryString ? `?${queryString}` : ''}`;

    return api.get<PaginatedResponse<any>>(endpoint);
  },

  getLocation: async (id: number) => {
    return api.get<any>(`/v1/locations/${id}`);
  },

  createLocation: async (data: {
    name: string;
    code: string;
    type?: 'receiving' | 'issuing';
    description?: string;
  }) => {
    return api.post<any>('/v1/locations', data);
  },

  updateLocation: async (id: number, data: Partial<{
    name: string;
    code: string;
    type?: 'receiving' | 'issuing';
    description?: string;
  }>) => {
    return api.put<any>(`/v1/locations/${id}`, data);
  },

  deleteLocation: async (id: number) => {
    return api.delete(`/v1/locations/${id}`);
  },
};

// Stock Transactions API
export const stockApi = {
  receiveStock: async (data: {
    location_id: number;
    supplier_id?: number;
    items: Array<{
      item_id?: number;
      quantity: number;
      unit_price?: number;
      cost_price?: number;
      batch_number: string;
      expiry_date?: string;
      new_item?: Record<string, unknown>;
    }>;
    notes?: string;
  }) => {
    return api.post<any>('/v1/stock/receive', data);
  },

  issueStock: async (data: {
    location_id: number;
    to_location_id?: number | null;
    to_patient_id?: number | null;
    visit_id?: number | null;
    items: Array<{
      item_id: number;
      quantity: number;
    }>;
    notes?: string;
  }) => {
    return api.post<any>('/v1/stock/issue', data);
  },

  getStockByLocation: async (params?: { location_id?: number; page?: number; per_page?: number }) => {
    const queryParams = new URLSearchParams();
    if (params?.location_id) queryParams.append('location_id', params.location_id.toString());
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.per_page) queryParams.append('per_page', params.per_page.toString());
    const qs = queryParams.toString();
    return api.get<PaginatedResponse<any>>(`/v1/stock/by-location${qs ? `?${qs}` : ''}`);
  },

  getTransactions: async (params?: {
    type?: 'receive' | 'issue';
    to_patient_id?: number;
    date_from?: string;
    date_to?: string;
    page?: number;
    per_page?: number;
  }) => {
    const queryParams = new URLSearchParams();
    if (params?.type) queryParams.append('type', params.type);
    if (params?.to_patient_id != null) queryParams.append('to_patient_id', params.to_patient_id.toString());
    if (params?.date_from) queryParams.append('date_from', params.date_from);
    if (params?.date_to) queryParams.append('date_to', params.date_to);
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.per_page) queryParams.append('per_page', params.per_page.toString());

    const queryString = queryParams.toString();
    const endpoint = `/v1/stock/transactions${queryString ? `?${queryString}` : ''}`;

    return api.get<PaginatedResponse<any>>(endpoint);
  },
};

// Dashboard API
export const dashboardApi = {
  getStats: async () => {
    return api.get<{
      overview: {
        total_items: number;
        total_categories: number;
        total_locations: number;
        total_stock_value: number;
        low_stock_items: number;
      };
      transactions: {
        today: number;
        this_month: number;
      };
      recent_transactions: any[];
    }>('/v1/dashboard/stats');
  },

  getStockMovement: async (days: number = 7) => {
    return api.get<Array<{
      date: string;
      type: 'receive' | 'issue' | 'adjust';
      total_quantity: number;
      total_items: number;
      items: Array<{
        item_id: number;
        item_name: string;
        quantity: number;
      }>;
    }>>(`/v1/dashboard/stock-movement?days=${days}`);
  },

  getLowStockAlerts: async () => {
    return api.get<Array<{
      id: number;
      name: string;
      current: number;
      minimum: number;
      category: string;
    }>>('/v1/dashboard/low-stock-alerts');
  },

  getRecentTransactions: async (limit: number = 10) => {
    return api.get<Array<{
      id: number;
      type: 'receive' | 'issue' | 'adjust';
      reference_number: string;
      location?: {
        id: number;
        name: string;
      };
      user?: {
        id: number;
        name: string;
        email: string;
      };
      items: Array<{
        id: number;
        item: {
          id: number;
          name: string;
        };
        quantity: number;
      }>;
      created_at: string;
    }>>(`/v1/dashboard/recent-transactions?limit=${limit}`);
  },
};

// Reports API
export const reportsApi = {
  getInventoryReport: async (params?: {
    category?: string;
    location?: string;
    status?: string;
  }) => {
    const queryParams = new URLSearchParams();
    if (params?.category) queryParams.append('category', params.category);
    if (params?.location) queryParams.append('location', params.location);
    if (params?.status) queryParams.append('status', params.status);

    const queryString = queryParams.toString();
    const endpoint = `/v1/reports/inventory${queryString ? `?${queryString}` : ''}`;

    return api.get<any>(endpoint);
  },

  getTransactionReport: async (params?: {
    type?: 'receive' | 'issue';
    start_date?: string;
    end_date?: string;
  }) => {
    const queryParams = new URLSearchParams();
    if (params?.type) queryParams.append('type', params.type);
    if (params?.start_date) queryParams.append('start_date', params.start_date);
    if (params?.end_date) queryParams.append('end_date', params.end_date);

    const queryString = queryParams.toString();
    const endpoint = `/v1/reports/transactions${queryString ? `?${queryString}` : ''}`;

    return api.get<any>(endpoint);
  },

  getRevenueReport: async (params?: {
    date_from?: string;
    date_to?: string;
    group_by?: 'day' | 'month';
  }) => {
    const queryParams = new URLSearchParams();
    if (params?.date_from) queryParams.append('date_from', params.date_from);
    if (params?.date_to) queryParams.append('date_to', params.date_to);
    if (params?.group_by) queryParams.append('group_by', params.group_by);
    const qs = queryParams.toString();
    return api.get<any>(`/v1/reports/revenue${qs ? `?${qs}` : ''}`);
  },

  getSalesReport: async (params?: {
    date_from?: string;
    date_to?: string;
    view?: 'summary' | 'detail' | 'by_category';
    per_page?: number;
    page?: number;
  }) => {
    const qs = new URLSearchParams();
    if (params?.date_from) qs.append('date_from', params.date_from);
    if (params?.date_to)   qs.append('date_to',   params.date_to);
    if (params?.view)      qs.append('view',       params.view);
    if (params?.per_page)  qs.append('per_page',   String(params.per_page));
    if (params?.page)      qs.append('page',        String(params.page));
    const q = qs.toString();
    return api.get<any>(`/v1/reports/sales${q ? `?${q}` : ''}`);
  },

  getDiscountsReport: async (params?: {
    date_from?: string;
    date_to?: string;
    per_page?: number;
    page?: number;
  }) => {
    const qs = new URLSearchParams();
    if (params?.date_from) qs.append('date_from', params.date_from);
    if (params?.date_to)   qs.append('date_to',   params.date_to);
    if (params?.per_page)  qs.append('per_page',   String(params.per_page));
    if (params?.page)      qs.append('page',        String(params.page));
    const q = qs.toString();
    return api.get<any>(`/v1/reports/discounts${q ? `?${q}` : ''}`);
  },

  getUtilizationReport: async (params?: {
    date_from?: string;
    date_to?: string;
    location_id?: number;
  }) => {
    const queryParams = new URLSearchParams();
    if (params?.date_from) queryParams.append('date_from', params.date_from);
    if (params?.date_to) queryParams.append('date_to', params.date_to);
    if (params?.location_id) queryParams.append('location_id', params.location_id.toString());
    const qs = queryParams.toString();
    return api.get<any>(`/v1/reports/utilization${qs ? `?${qs}` : ''}`);
  },

  getUtilizationDetailReport: async (params?: {
    date_from?: string;
    date_to?: string;
    location_id?: number;
    item_id?: number;
    page?: number;
    per_page?: number;
  }) => {
    const queryParams = new URLSearchParams();
    if (params?.date_from) queryParams.append('date_from', params.date_from);
    if (params?.date_to) queryParams.append('date_to', params.date_to);
    if (params?.location_id) queryParams.append('location_id', params.location_id.toString());
    if (params?.item_id) queryParams.append('item_id', params.item_id.toString());
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.per_page) queryParams.append('per_page', params.per_page.toString());
    const qs = queryParams.toString();
    return api.get<any>(`/v1/reports/utilization/detail${qs ? `?${qs}` : ''}`);
  },

  getStockBatchReport: async (params?: {
    location_id?: number;
    item_id?: number;
    item_search?: string;
    batch_number?: string;
    include_empty?: boolean;
    page?: number;
    per_page?: number;
  }) => {
    const qs = new URLSearchParams();
    if (params?.location_id) qs.append('location_id', String(params.location_id));
    if (params?.item_id)     qs.append('item_id',     String(params.item_id));
    if (params?.item_search) qs.append('item_search', params.item_search);
    if (params?.batch_number) qs.append('batch_number', params.batch_number);
    if (params?.include_empty) qs.append('include_empty', '1');
    if (params?.page)        qs.append('page',        String(params.page));
    if (params?.per_page)    qs.append('per_page',    String(params.per_page));
    const q = qs.toString();
    return api.get<any>(`/v1/reports/stock-batches${q ? `?${q}` : ''}`);
  },
};

// Visits API
export const visitsApi = {
  getVisits: async (params?: {
    patient_id?: number;
    status?: 'open' | 'closed';
    page?: number;
    per_page?: number;
  }) => {
    const queryParams = new URLSearchParams();
    if (params?.patient_id != null) queryParams.append('patient_id', params.patient_id.toString());
    if (params?.status) queryParams.append('status', params.status);
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.per_page) queryParams.append('per_page', params.per_page.toString());
    const qs = queryParams.toString();
    return api.get<PaginatedResponse<any>>(`/v1/visits${qs ? `?${qs}` : ''}`);
  },

  createVisit: async (data: {
    patient_id: number;
    visit_type: 'outpatient' | 'inpatient';
    notes?: string;
  }) => {
    return api.post<any>('/v1/visits', data);
  },

  getVisit: async (id: number) => {
    return api.get<any>(`/v1/visits/${id}`);
  },

  closeVisit: async (id: number) => {
    return api.patch<any>(`/v1/visits/${id}/close`);
  },

  reopenVisit: async (id: number) => {
    return api.patch<any>(`/v1/visits/${id}/reopen`);
  },

  updateLineDiscount: async (visitId: number, data: {
    item_id: number;
    unit_price: number;
    discount_type: 'percentage' | 'fixed';
    discount_value: number;
  }) => {
    return api.patch<any>(`/v1/visits/${visitId}/discount`, data);
  },

  removeLineDiscount: async (visitId: number, itemId: number, unitPrice: number) => {
    return api.delete(`/v1/visits/${visitId}/discount/${itemId}?unit_price=${unitPrice}`);
  },
};

// Patients API (for issue-to-patient)
export const patientsApi = {
  getPatients: async (params?: { search?: string; page?: number; per_page?: number }) => {
    const queryParams = new URLSearchParams();
    if (params?.search) queryParams.append('search', params.search);
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.per_page) queryParams.append('per_page', params.per_page.toString());
    const qs = queryParams.toString();
    return api.get<PaginatedResponse<any>>(`/v1/patients${qs ? `?${qs}` : ''}`);
  },
  createPatient: async (data: { name: string; identifier?: string }) => {
    return api.post<any>('/v1/patients', data);
  },
  findOrCreatePatient: async (data: { name: string; identifier?: string; date_of_birth: string }) => {
    return api.post<any>('/v1/patients/find-or-create', data);
  },
  getPatient: async (id: number) => api.get<any>(`/v1/patients/${id}`),
};

// Locations API Types
export interface Location {
  id: number;
  name: string;
  code: string;
  type?: 'receiving' | 'issuing';
  description?: string;
  created_at?: string;
  updated_at?: string;
}

export const usersApi = {
  getUsers: async (params?: {
    page?: number;
    search?: string;
    role?: 'admin' | 'staff';
    status?: 'active' | 'inactive';
    per_page?: number;
  }) => {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.search) queryParams.append('search', params.search);
    if (params?.role) queryParams.append('role', params.role);
    if (params?.status) queryParams.append('status', params.status);
    if (params?.per_page) queryParams.append('per_page', params.per_page.toString());

    const queryString = queryParams.toString();
    const endpoint = `/v1/users${queryString ? `?${queryString}` : ''}`;

    return api.get<PaginatedResponse<User>>(endpoint);
  },

  getUser: async (id: number) => {
    return api.get<User>(`/v1/users/${id}`);
  },

  createUser: async (data: {
    name: string;
    email: string;
    password: string;
    role?: 'admin' | 'staff';
    status?: 'active' | 'inactive';
  }) => {
    return api.post<User>('/v1/users', data);
  },

  updateUser: async (id: number, data: Partial<{
    name: string;
    email: string;
    password?: string;
    role?: 'admin' | 'staff';
    status?: 'active' | 'inactive';
  }>) => {
    return api.put<User>(`/v1/users/${id}`, data);
  },

  deleteUser: async (id: number) => {
    return api.delete(`/v1/users/${id}`);
  },

  updateUserRole: async (id: number, role: 'admin' | 'staff') => {
    return api.put<User>(`/v1/users/${id}/role`, { role });
  },

  updateUserStatus: async (id: number, status: 'active' | 'inactive') => {
    return api.put<User>(`/v1/users/${id}/status`, { status });
  },
};

// Supplier API
export const supplierApi = {
  search: async (params?: { search?: string; is_active?: boolean; per_page?: number }) => {
    const q = new URLSearchParams();
    if (params?.search) q.append('search', params.search);
    if (params?.is_active !== undefined) q.append('is_active', params.is_active ? '1' : '0');
    if (params?.per_page) q.append('per_page', String(params.per_page));
    const qs = q.toString();
    return api.get<any>(`/v1/suppliers${qs ? `?${qs}` : ''}`);
  },

  create: async (data: { name: string; phone?: string; email?: string }) => {
    return api.post<any>('/v1/suppliers', data);
  },

  update: async (id: number, data: { name?: string; phone?: string; email?: string; is_active?: boolean }) => {
    return api.put<any>(`/v1/suppliers/${id}`, data);
  },
};

